import React, { useMemo } from 'react';
import InputMask from 'react-input-mask';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isFunction, isEqual, isEmpty } from 'lodash';
import { fieldProps, getPreferredProp } from '../utils';

/**
 * Input Component
 *
 */
const InputField = (props) => {
  const {
    autoComplete = 'on',
    autoFocus = false,
    className = '',
    dataTestId = '',
    disabled = false,
    hideError = false,
    hideHint = false,
    hideLabel = false,
    hint = '',
    inline = false,
    labelClassName = '',
    mask = '',
    max = null,
    min = null,
    maxLength = 99,
    placeholder = '',
    required = false,
    step = 'any',
    style = {
      container: {},
      label: {},
      input: {},
      hint: {},
      error: {},
    },
    type = 'text',
    id,
    label,
    omitLabelStyles = false
  } = props;

  const onChange = (event) => {
    const callback = getPreferredProp(props, 'onChange');

    if (isFunction(callback)) {
      callback(event);
    }
    if (isFunction(props.onChange) && !isEqual(props.onChange, callback)) {
      props.onChange(event);
    }
  };

  const hasError = getPreferredProp(props, 'invalid') && getPreferredProp(props, 'touched');
  const name = getPreferredProp(props, 'name');
  const error = getPreferredProp(props, 'error');

  const containerClass = classNames({
    'app-components-form-field--has-error': hasError,
  }, className);

  const inputClass = classNames({
    'h-8 bg-white rounded border text-brand-blue font-sans text-sm tracking-tight px-2 w-full': true,
    'mb-2.5 w-full': true,
    'flex flex-row flex-nowrap': inline,
    'border-light-border-blue': !hasError,
    'text-red border border-red': hasError,
    'opacity-50': disabled,
    'app-components-form-field--has-error': hasError,
  }, className);

  const labelClass = useMemo(() => classNames({
    'text-xs mb-0 normal-case tracking-normal font-semibold leading-4 flex-none text-right mr-2.5 pt-[14px]': !omitLabelStyles,
    'text-dark-grey': !hasError && !omitLabelStyles,
    'text-red': hasError && !omitLabelStyles,
    'sr-only': hideLabel,
    'ui-form-field__label--required': required
  }, labelClassName, style.label), [ required, hasError, labelClassName, hideLabel]);

  return (
    <div
      className={containerClass}
      style={style.container}
    >
      <label
        htmlFor={id}
        className={labelClass}
        style={style.label}
      >
        {label}
      </label>
      <div>
        {mask ? (
          <InputMask
            className={inputClass}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            disabled={disabled}
            id={id}
            name={name}
            onBlur={getPreferredProp(props, 'onBlur')}
            onChange={onChange}
            onFocus={getPreferredProp(props, 'onFocus')}
            placeholder={placeholder}
            style={style.input}
            type={type}
            value={getPreferredProp(props, 'value')}
            mask={mask}
            maskChar="_"
          />
        ) : (
          <input
            className={inputClass}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            disabled={disabled}
            id={id}
            max={max}
            maxLength={maxLength}
            min={min}
            name={name}
            onBlur={getPreferredProp(props, 'onBlur')}
            onChange={onChange}
            onFocus={getPreferredProp(props, 'onFocus')}
            placeholder={placeholder}
            step={step}
            style={style.input}
            type={type}
            value={getPreferredProp(props, 'value')}
            data-testid={dataTestId}
          />
        )}
        {!hideHint && hint && (
          <div className="ui-form-field__hint" style={style.hint}>
            {hint}
          </div>
        )}
        {!hideError && error && (
          <div
            className="app-components-form-field__error"
            style={style.error}
            data-test-visible={hasError}
            data-testid={`${name || id || 'unnamed'}-error`}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

InputField.propTypes = {
  autoComplete: PropTypes.string.isRequired,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  hideLabel: PropTypes.bool,
  hint: PropTypes.node,
  id: PropTypes.string.isRequired,
  inline: PropTypes.bool,
  label: PropTypes.node.isRequired,
  mask: PropTypes.string,
  placeholder: PropTypes.string,
  hideHint: PropTypes.bool,
  hideError: PropTypes.bool,
  required: PropTypes.bool,
  step: PropTypes.string,
  style: PropTypes.shape({
    container: PropTypes.object,
    error: PropTypes.object,
    hint: PropTypes.object,
    input: PropTypes.object,
    label: PropTypes.object,
  }),
  type: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  dataTestId: PropTypes.string,
  omitLabelStyles: PropTypes.bool,
  ...fieldProps,
};

export default InputField;
